import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import uPlot from 'uplot'

export interface CursorDataPoint {
  sensorId: string
  yValue: number | null
  xValue: number | string | null
  unit?: string
}

interface CursorDataContextType {
  cursorData: Record<string, CursorDataPoint>
  setCursorData: React.Dispatch<
    React.SetStateAction<Record<string, CursorDataPoint>>
  >
  updateCursorData: (
    sensorId: string,
    yValue: number | null,
    xValue: number | null,
    unit?: string
  ) => void
  clearCursorData: () => void
  getXValueAtCursor: (
    e: React.MouseEvent,
    uPlotInstance: uPlot | null
  ) => number | null
  /**
   * Label the user is pointing at from outside the chart (a Labels tab row), so
   * the overlay can promote its flag above the stack. Pointer-frequency state
   * lives here rather than on `ScatterplotContext`, whose value is consumed by
   * the whole chart tree — routing hover through that re-renders everything
   * twice per row crossed.
   */
  hoveredLabelId: string | null
  setHoveredLabelId: React.Dispatch<React.SetStateAction<string | null>>
}

const CursorDataContext = createContext<CursorDataContextType | undefined>(
  undefined
)

interface CursorDataProviderProps {
  children: ReactNode
}

export const CursorDataProvider: React.FC<CursorDataProviderProps> = ({
  children,
}) => {
  const [cursorData, setCursorData] = useState<Record<string, CursorDataPoint>>(
    {}
  )
  const [hoveredLabelId, setHoveredLabelId] = useState<string | null>(null)

  const updateCursorData = useCallback(
    (
      sensorId: string,
      yValue: number | null,
      xValue: number | null,
      unit?: string
    ) => {
      setCursorData((prev) => ({
        ...prev,
        [sensorId]: { sensorId, yValue, xValue, unit },
      }))
    },
    []
  )

  const clearCursorData = useCallback(() => {
    setCursorData({})
  }, [])

  const getXValueAtCursor = useCallback(
    (e: React.MouseEvent, uPlotInstance: uPlot | null): number | null => {
      if (!uPlotInstance) return null

      const overRect = uPlotInstance.over.getBoundingClientRect()
      const relativeX = e.clientX - overRect.left
      return uPlotInstance.posToVal(relativeX, 'x')
    },
    []
  )

  // Memoized: this provider carries pointer-frequency state, so an inline value
  // would re-render every consumer on each cursor or hover change.
  const value = useMemo(
    () => ({
      cursorData,
      setCursorData,
      updateCursorData,
      clearCursorData,
      getXValueAtCursor,
      hoveredLabelId,
      setHoveredLabelId,
    }),
    [
      cursorData,
      updateCursorData,
      clearCursorData,
      getXValueAtCursor,
      hoveredLabelId,
    ]
  )

  return (
    <CursorDataContext.Provider value={value}>
      {children}
    </CursorDataContext.Provider>
  )
}

export const useCursorData = (): CursorDataContextType => {
  const context = useContext(CursorDataContext)
  if (context === undefined) {
    throw new Error('useCursorData must be used within a CursorDataProvider')
  }
  return context
}
